import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  User, Store, Check, ArrowRight, ArrowLeft, Loader2,
  Gem, MapPin, Sparkles, Eye, EyeOff, Briefcase,
} from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/store';
import { AuthLayout, GoogleIcon, LinkedInIcon } from './Login';
import CountryCityPicker from '../components/CountryCityPicker';
import CharacterSelect from '../components/CharacterSelect';
import OccupationPicker from '../components/OccupationPicker';
import { themeOf } from '../lib/classTheme';
import { getTimezone } from '../lib/countries';

const STEPS = [
  { n: 1, label: 'Account', icon: User },
  { n: 2, label: 'Location', icon: MapPin },
  { n: 3, label: 'About you', icon: Briefcase },
  { n: 4, label: 'Hero', icon: Sparkles },
];

export default function Register() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const setToken = useAuth(s => s.setToken);

  const [step, setStep] = useState(1);
  const [showPw, setShowPw] = useState(false);
  const [refUser, setRefUser] = useState(null);
  const [checking, setChecking] = useState(false);
  const [avail, setAvail] = useState({});

  const [f, setF] = useState({
    email: '', username: '', password: '', displayName: '',
    role: sp.get('role') === 'producer' ? 'producer' : 'user',
    producerKind: 'local',
    referralCode: sp.get('ref') || '',
  });

  const [loc, setLoc] = useState({
    countryCode: null, countryName: null, countryGroupId: null,
    cityGroupId: null, cityName: null, cityHandle: null,
    timezone: getTimezone(),
  });

  const [occ, setOcc] = useState({ occupation: null });
  const [classKey, setClassKey] = useState(null);
  const [templateId, setTemplateId] = useState(null);

  /* referral lookup */
  useEffect(() => {
    const ref = sp.get('ref');
    if (ref) {
      api.get(`/referrals/validate/${ref}`)
        .then(r => r.data.valid && setRefUser(r.data.user))
        .catch(() => {});
    }
  }, []);

  /* live availability check */
  useEffect(() => {
    if (!f.username && !f.email) return;
    const t = setTimeout(async () => {
      setChecking(true);
      try {
        const { data } = await api.post('/auth/check', {
          username: f.username || undefined,
          email: f.email && /\S+@\S+\.\S+/.test(f.email) ? f.email : undefined,
        });
        setAvail(data);
      } catch {} finally { setChecking(false); }
    }, 600);
    return () => clearTimeout(t);
  }, [f.username, f.email]);

  const register = useMutation({
    mutationFn: () => api.post('/auth/register', {
      ...f,
      cityGroupId: loc.cityGroupId || undefined,
      countryGroupId: loc.countryGroupId || undefined,
      countryCode: loc.countryCode || undefined,
      timezone: loc.timezone || undefined,
      occupation: occ.occupation || undefined,
      occupationDetail: occ.occupation_detail || undefined,
      companyName: occ.company_name || undefined,
      companyWebsite: occ.company_website || undefined,
      templateId: f.role === 'user' ? templateId : undefined,
      classKey: f.role === 'user' ? classKey : undefined,
      referralCode: f.referralCode || undefined,
    }),
    onSuccess: async ({ data }) => {
      await setToken(data.token);
      toast.success(
        data.hero
          ? `Welcome, ${data.hero.name}! ⚔️`
          : 'Welcome to Wall of Freedom! 🎉',
        { duration: 5000 });
      nav(f.role === 'producer' ? '/create-shop' : '/hero', { replace: true });
    },
    onError: e => toast.error(e.message),
  });

  /* validation */
  const step1Valid =
    /\S+@\S+\.\S+/.test(f.email) &&
    f.username.length >= 3 &&
    f.password.length >= 6 &&
    avail.emailAvailable !== false &&
    avail.usernameAvailable !== false;
  const step2Valid = !!loc.cityGroupId;
  const step3Valid = !!occ.occupation;
  const step4Valid = f.role === 'producer' || !!templateId;

  const maxStep = f.role === 'producer' ? 3 : 4;
  const canNext = step === 1 ? step1Valid
    : step === 2 ? step2Valid
    : step === 3 ? step3Valid : step4Valid;

  const oauthQuery = new URLSearchParams({
    ...(f.referralCode ? { ref: f.referralCode } : {}),
    ...(loc.cityGroupId ? { cityGroupId: String(loc.cityGroupId) } : {}),
    ...(loc.countryGroupId ? { countryGroupId: String(loc.countryGroupId) } : {}),
    ...(templateId ? { templateId: String(templateId) } : {}),
  }).toString();

  const t = classKey ? themeOf(classKey) : null;

  return (
    <AuthLayout
      title={
        step === 1 ? 'Create your account' :
        step === 2 ? 'Where are you?' :
        step === 3 ? 'Tell us about you' : 'Choose your hero'
      }
      sub={
        step === 1 ? 'Free forever. Earn points by shopping local.' :
        step === 2 ? 'Join your city community' :
        step === 3 ? 'Helps us personalise your experience' : 'Pick a class and a champion'
      }
      wide={step === 4}
    >
      {/* ─── PROGRESS ─── */}
      <div className="flex items-center gap-1.5 mb-5">
        {STEPS.slice(0, maxStep).map((s, i) => (
          <div key={s.n} className="flex items-center flex-1">
            <div className={`flex items-center gap-1.5 ${step >= s.n ? 'text-brand-600' : 'text-ink-300'}`}>
              <div className={`w-7 h-7 rounded-full grid place-items-center text-[11px] font-black transition-all
                ${step > s.n ? 'bg-mint-500 text-white'
                  : step === s.n ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/30'
                  : 'bg-ink-100 text-ink-400'}`}>
                {step > s.n ? <Check size={13} strokeWidth={3} /> : s.n}
              </div>
              <span className="text-[10px] font-black uppercase tracking-wide hidden sm:block">
                {s.label}
              </span>
            </div>
            {i < maxStep - 1 && (
              <div className={`flex-1 h-0.5 mx-1.5 rounded transition-all
                ${step > s.n ? 'bg-mint-500' : 'bg-ink-100'}`} />
            )}
          </div>
        ))}
      </div>

      {/* ─── REFERRAL BANNER ─── */}
      {refUser && step === 1 && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className="mb-4 flex items-center gap-3 bg-mint-400/10 border border-mint-400/30 rounded-xl p-3">
          <img src={refUser.avatar_url || `https://api.dicebear.com/9.x/adventurer/svg?seed=${refUser.username}`}
            className="w-9 h-9 rounded-full bg-white" alt="" />
          <div className="text-sm">
            <p className="font-bold">{refUser.display_name || refUser.username} invited you</p>
            <p className="text-mint-600 text-xs font-semibold">You both earn bonus points 🎁</p>
          </div>
        </motion.div>
      )}

      <AnimatePresence mode="wait">
        {/* ══════════ STEP 1 ══════════ */}
        {step === 1 && (
          <motion.div key="s1" initial={{ opacity: 0, x: -18 }} animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 18 }} className="space-y-4">

            <div className="grid grid-cols-2 gap-2">
              <RoleCard active={f.role === 'user'} onClick={() => setF({ ...f, role: 'user' })}
                icon={User} title="Shopper / Gamer" sub="Get a hero, earn points" />
              <RoleCard active={f.role === 'producer'} onClick={() => setF({ ...f, role: 'producer' })}
                icon={Store} title="Producer / Shop" sub="Sell & reward customers" />
            </div>

            <div className="grid gap-2">
              <a href={`/api/auth/google?${oauthQuery}`} className="btn-ghost w-full py-2.5">
                <GoogleIcon /> Sign up with Google
              </a>
              <a href={`/api/auth/linkedin?${oauthQuery}`} className="btn-ghost w-full py-2.5">
                <LinkedInIcon /> Sign up with LinkedIn
              </a>
            </div>

            <div className="flex items-center gap-3">
              <div className="h-px bg-ink-200 flex-1" />
              <span className="text-xs font-bold text-ink-400">OR</span>
              <div className="h-px bg-ink-200 flex-1" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Username *</label>
                <div className="relative">
                  <input className={`input pr-8 ${avail.usernameAvailable === false ? '!border-red-400' : ''}`}
                    value={f.username} minLength={3}
                    onChange={e => setF({ ...f, username: e.target.value.replace(/[^a-zA-Z0-9_]/g, '').toLowerCase() })} />
                  <Status ok={avail.usernameAvailable} busy={checking} />
                </div>
                {avail.usernameAvailable === false && avail.suggestion && (
                  <button type="button" onClick={() => setF({ ...f, username: avail.suggestion })}
                    className="text-[11px] font-bold text-brand-600 mt-1">
                    Try “{avail.suggestion}”
                  </button>
                )}
              </div>
              <div>
                <label className="label">Display name</label>
                <input className="input" value={f.displayName}
                  onChange={e => setF({ ...f, displayName: e.target.value })} />
              </div>
            </div>

            <div>
              <label className="label">Email *</label>
              <div className="relative">
                <input className={`input pr-8 ${avail.emailAvailable === false ? '!border-red-400' : ''}`}
                  type="email" value={f.email}
                  onChange={e => setF({ ...f, email: e.target.value })} />
                <Status ok={avail.emailAvailable} busy={checking} />
              </div>
              {avail.emailAvailable === false && (
                <p className="text-[11px] font-bold text-red-500 mt-1">
                  Already registered — <Link to="/login" className="underline">log in instead</Link>
                </p>
              )}
            </div>

            <div>
              <label className="label">Password *</label>
              <div className="relative">
                <input className="input pr-10" type={showPw ? 'text' : 'password'}
                  value={f.password} minLength={6}
                  onChange={e => setF({ ...f, password: e.target.value })} />
                <button type="button" onClick={() => setShowPw(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400">
                  {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {f.password && f.password.length < 6 && (
                <p className="text-[11px] text-ink-400 font-semibold mt-1">At least 6 characters</p>
              )}
            </div>

            {f.role === 'producer' && (
              <div>
                <label className="label">Business type</label>
                <div className="grid grid-cols-3 gap-2">
                  {[['local', '🌱 Local'], ['national', '🏭 National'], ['online_shop', '🛒 Online']].map(([k, l]) => (
                    <button type="button" key={k} onClick={() => setF({ ...f, producerKind: k })}
                      className={`py-2 rounded-xl text-xs font-bold border-2 transition
                        ${f.producerKind === k
                          ? 'bg-mint-500 text-white border-mint-500'
                          : 'bg-white border-ink-200 text-ink-600'}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!sp.get('ref') && (
              <div>
                <label className="label">Referral code (optional)</label>
                <input className="input uppercase tracking-wider" value={f.referralCode}
                  placeholder="ABC123"
                  onChange={e => setF({ ...f, referralCode: e.target.value.toUpperCase() })} />
              </div>
            )}
          </motion.div>
        )}

        {/* ══════════ STEP 2 ══════════ */}
        {step === 2 && (
          <motion.div key="s2" initial={{ opacity: 0, x: -18 }} animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 18 }}>
            <CountryCityPicker value={loc} onChange={setLoc} />
          </motion.div>
        )}

        {/* ══════════ STEP 3 — OCCUPATION ══════════ */}
        {step === 3 && (
          <motion.div key="s3" initial={{ opacity: 0, x: -18 }} animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 18 }}>
            <OccupationPicker value={occ} onChange={setOcc} />
          </motion.div>
        )}

        {/* ══════════ STEP 4 — HERO ══════════ */}
        {step === 4 && (
          <motion.div key="s4" initial={{ opacity: 0, x: -18 }} animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 18 }}>
            <CharacterSelect
              classKey={classKey} templateId={templateId}
              onClass={setClassKey} onTemplate={setTemplateId} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── NAVIGATION ─── */}
      <div className="flex gap-2 mt-6">
        {step > 1 && (
          <button type="button" onClick={() => setStep(s => s - 1)} className="btn-ghost">
            <ArrowLeft size={15} /> Back
          </button>
        )}

        {step < maxStep ? (
          <button type="button" onClick={() => canNext && setStep(s => s + 1)} disabled={!canNext}
            className="btn-primary flex-1 py-3">
            Continue <ArrowRight size={15} />
          </button>
        ) : (
          <button type="button" onClick={() => register.mutate()}
            disabled={!canNext || register.isPending}
            className="btn-primary flex-1 py-3 font-black"
            style={t && canNext ? { boxShadow: t.glowCss } : {}}>
            {register.isPending
              ? <><Loader2 size={16} className="animate-spin" /> Creating…</>
              : <><Sparkles size={16} /> Start playing</>}
          </button>
        )}
      </div>

      {step === maxStep && (
        <p className="text-[11px] text-center text-ink-400 font-semibold mt-3">
          <Gem size={10} className="inline" /> You start with 100 points + a free weapon
        </p>
      )}

      <p className="text-center text-sm text-ink-500 mt-5">
        Already have an account? <Link to="/login" className="font-bold text-brand-600">Log in</Link>
      </p>
    </AuthLayout>
  );
}

/* ---------------- helpers ---------------- */
function RoleCard({ active, onClick, icon: Icon, title, sub }) {
  return (
    <button type="button" onClick={onClick}
      className={`relative p-3 rounded-xl border-2 text-left transition
        ${active ? 'border-brand-500 bg-brand-50' : 'border-ink-200 bg-white hover:border-ink-300'}`}>
      {active && <Check size={13} className="absolute top-2 right-2 text-brand-600" strokeWidth={3} />}
      <Icon size={20} className={active ? 'text-brand-600' : 'text-ink-400'} />
      <p className="font-bold text-sm mt-1.5">{title}</p>
      <p className="text-[11px] text-ink-500 leading-tight">{sub}</p>
    </button>
  );
}

function Status({ ok, busy }) {
  if (busy) return <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-ink-300" />;
  if (ok === true) return <Check size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-mint-500" strokeWidth={3} />;
  if (ok === false) return <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500 font-black text-sm">×</span>;
  return null;
}